import axios from "axios";
import {
  API_URLS,
  LOCALSTORAGE_TOKEN_ITEM,
  SUNO_API_BASE_URL,
  SUNO_API_STORAGE_URL,
} from "constants/api";

function normalizeSuggestionList(input: unknown): string[] {
  if (!input) return [];
  if (Array.isArray(input)) {
    return input
      .map((item) => {
        if (typeof item === "string") {
          return item.trim();
        }
        if (item && typeof item === "object") {
          const candidate =
            (item as Record<string, unknown>).text ??
            (item as Record<string, unknown>).title ??
            (item as Record<string, unknown>).prompt ??
            (item as Record<string, unknown>).value ??
            (item as Record<string, unknown>).content;
          if (typeof candidate === "string") {
            return candidate.trim();
          }
        }
        return "";
      })
      .filter(Boolean);
  }
  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return normalizeSuggestionList(parsed);
      }
    } catch {
      // ignore JSON parse errors
    }
    return trimmed
      .split(/\r?\n|[,;•]|\u2022/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
}

export type Reaction = 'like' | 'dislike' | null;


// СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ СХЕМЫ 
export type Track = {
  id: string;
  title: string;
  createdAt: number;
  description?: string;
  params?: Record<string, any>;
  durationSec?: number;
  reaction?: Reaction;
  personas?: string; 
  hasPersonas?: boolean;
  fullSongs?: boolean;
  trashed?: boolean; 
};

export type PersonaSummary = {
  trackId?: string;
  name: string;
  description?: string;
  personaId?: string | null;
  taskId?: string | null;
  audioId?: string | null;
  imageUrl?: string | null;
  id?: number;
  trashed?: boolean;
};

type SunoPersonResponse = {
  id: number;
  suno_person_id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  trashed: boolean;
  tracks: unknown[];
};

type SunoPersonsListResponse = {
  status: boolean;
  data: {
    persons: SunoPersonResponse[];
  };
  message: string;
};

export type Workspace = {
  id: string;
  name: string;
  createdAt: number;
  trackIds: string[];
  trashed?: boolean;
  remoteId?: number;
  uuid?: string;
  userId?: number;
  updatedAt?: number;
};

export type Playlist = {
  id: string;
  name: string;
  createdAt: number;
  trackIds: string[]; 
  coverImage?: string;
  trashed?: boolean;
  updatedAt?: number;
  remoteId?: number;
  uuid?: string;
  userId?: number;
};

export type Database = {
  workspaces: Record<string, Workspace>;
  tracks: Record<string, Track>;
  playlists: Record<string, Playlist>;
};

const STORAGE_KEY_BASE = "aiCompose.db";

function getStorageKey(): string {
  try {
    const raw = localStorage.getItem("aiCompose.userId");
    const userId = raw && raw.trim().length ? raw.trim() : null;
    if (!userId) return STORAGE_KEY_BASE;
    return `${STORAGE_KEY_BASE}.user.${userId}`;
  } catch {
    return STORAGE_KEY_BASE;
  }
}

type SunoWorkspacePayload = {
  id: number;
  user_id: number;
  uuid: string | null;
  name: string | null;
  trashed: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  tracks?: any[] | null;
};

type SunoWorkspaceListResponse = {
  success: boolean;
  data?: {
    workspaces?: SunoWorkspacePayload[] | Record<string, SunoWorkspacePayload>;
  };
  links?: unknown;
  message?: string;
};

function extractWorkspaceArray(data?: SunoWorkspaceListResponse["data"]): SunoWorkspacePayload[] {
  const workspaces = data?.workspaces;
  if (!workspaces) return [];
  if (Array.isArray(workspaces)) return workspaces;
  if (typeof workspaces === "object" && workspaces !== null) {
    return Object.values(workspaces as Record<string, SunoWorkspacePayload>);
  }
  return [];
}

function normalizeSunoWorkspace(
  payload: SunoWorkspacePayload,
  existing?: Workspace,
  fallbackName = "Untitled Workspace"
): Workspace {
  const workspaceId = payload.uuid || existing?.id || `suno_${payload.id}`;
  const createdAtRaw = payload.created_at ? new Date(payload.created_at).getTime() : NaN;
  const updatedAtRaw = payload.updated_at ? new Date(payload.updated_at).getTime() : NaN;
  const safeCreatedAt = Number.isFinite(createdAtRaw) ? createdAtRaw : existing?.createdAt ?? Date.now();
  const safeUpdatedAt = Number.isFinite(updatedAtRaw) ? updatedAtRaw : existing?.updatedAt;

  return {
    id: workspaceId,
    name: payload.name?.trim() || existing?.name || fallbackName,
    createdAt: safeCreatedAt,
    updatedAt: safeUpdatedAt,
    trackIds: existing?.trackIds ?? [],
    trashed: payload.trashed ?? existing?.trashed ?? false,
    remoteId: payload.id,
    uuid: payload.uuid ?? existing?.uuid,
    userId: payload.user_id ?? existing?.userId,
  };
}

function upsertWorkspaceFromPayload(db: Database, payload: SunoWorkspacePayload, fallbackName?: string): Workspace {
  const candidates = Object.values(db.workspaces);
  const existing =
    (payload.uuid && db.workspaces[payload.uuid]) ||
    candidates.find(ws => ws.remoteId === payload.id) ||
    candidates.find(ws => ws.uuid && payload.uuid && ws.uuid === payload.uuid);

  const normalized = normalizeSunoWorkspace(payload, existing, fallbackName);
  db.workspaces[normalized.id] = normalized;
  return normalized;
}

type SunoPlaylistPayload = {
  id: number;
  user_id: number;
  uuid: string | null;
  name: string | null;
  cover_image: string | null;
  trashed: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  tracks?: Array<{
    id?: number;
    uuid?: string;
    suno_audio_id?: string;
    [key: string]: any;
  }> | null;
};

type SunoPlaylistListResponse = {
  success: boolean;
  data?: {
    playlists?: SunoPlaylistPayload[] | Record<string, SunoPlaylistPayload>;
  };
  links?: unknown;
  message?: string;
};

type SunoPlaylistApiResponse = {
  success: boolean;
  data: SunoPlaylistPayload;
  links?: unknown;
  message?: string;
};

type SunoGenerateRequest = {
  customMode: boolean;
  instrumental: boolean;
  model: string;
  prompt?: string | null;
  style?: string | null;
  title?: string | null;
  personaId?: string | null;
  negativeTags?: string | null;
  vocalGender?: "m" | "f" | null;
  styleWeight?: number | null;
  weirdnessConstraint?: number | null;
  audioWeight?: number | null;
  workspace_id?: number | string | null;
};

type SunoGenerateResponse = {
  success: boolean;
  data?: {
    jobId?: string;
    taskId?: string;
    status?: string;
  };
  message?: string;
};

type SunoGenerationRecordResponse = {
  success: boolean;
  data?: {
    job?: {
      id: number;
      user_id: number;
      type: string;
      status: string;
      task_id: string;
      parent_music_id: string | null;
      request_payload: unknown;
      response_payload: unknown;
      error_code: string | null;
      error_message: string | null;
      created_at: string;
      updated_at: string;
    };
    tracks?: Record<string, any> | any[];
    links?: unknown;
  };
  links?: unknown;
  message?: string;
};

// Release-track specific helpers have been intentionally omitted for AI Compose.

function extractPlaylistArray(data?: SunoPlaylistListResponse["data"]): SunoPlaylistPayload[] {
  const playlists = data?.playlists;
  if (!playlists) return [];
  if (Array.isArray(playlists)) return playlists;
  if (typeof playlists === "object" && playlists !== null) {
    return Object.values(playlists as Record<string, SunoPlaylistPayload>);
  }
  return [];
}

function normalizeSunoPlaylist(
  payload: SunoPlaylistPayload,
  existing?: Playlist,
  fallbackName = "Untitled",
  fallbackCoverImage?: string,
  db?: Database
): Playlist {
  // Use numeric ID from payload as playlistId (primary identifier)
  const playlistId = payload.id ? `${payload.id}` : (existing?.id || `suno_pl_${Date.now()}`);
  
  // Convert dates
  const createdAtRaw = payload.created_at ? new Date(payload.created_at).getTime() : NaN;
  const updatedAtRaw = payload.updated_at ? new Date(payload.updated_at).getTime() : NaN;
  const safeCreatedAt = Number.isFinite(createdAtRaw) ? createdAtRaw : existing?.createdAt ?? Date.now();
  const safeUpdatedAt = Number.isFinite(updatedAtRaw) ? updatedAtRaw : existing?.updatedAt;

  // Process tracks from API response - convert to local track IDs
  let trackIds: string[] = [];
  
  if (payload.tracks && Array.isArray(payload.tracks) && payload.tracks.length > 0) {
    const trackDb = db || loadDb();
    
    for (const trackPayload of payload.tracks) {
      // Handle track object from API
      if (trackPayload && typeof trackPayload === 'object') {
        let foundTrack: Track | undefined;
        
        // Try to find by UUID first
        if (trackPayload.uuid) {
          foundTrack = Object.values(trackDb.tracks).find(
            t => t.params?.uuid === trackPayload.uuid || 
                 t.params?.sunoAudioId === trackPayload.uuid ||
                 t.id === trackPayload.uuid
          );
        }
        
        // Try by suno_audio_id
        if (!foundTrack && trackPayload.suno_audio_id) {
          foundTrack = Object.values(trackDb.tracks).find(
            t => t.params?.sunoAudioId === trackPayload.suno_audio_id ||
                 t.params?.uuid === trackPayload.suno_audio_id
          );
        }
        
        // Try by numeric ID
        if (!foundTrack && trackPayload.id) {
          foundTrack = Object.values(trackDb.tracks).find(
            t => t.params?.sunoTrackId === trackPayload.id
          );
        }
        
        if (foundTrack && !foundTrack.trashed) {
          trackIds.push(foundTrack.id);
        }
      }
    }
  } else {
    // If no tracks from API, use existing trackIds
    trackIds = existing?.trackIds ?? [];
  }

  return {
    id: playlistId,
    name: payload.name?.trim() || existing?.name || fallbackName,
    createdAt: safeCreatedAt,
    updatedAt: safeUpdatedAt,
    coverImage:
      resolveSunoAssetUrl(payload.cover_image) ??
      resolveSunoAssetUrl(existing?.coverImage) ??
      resolveSunoAssetUrl(fallbackCoverImage),
    trackIds,
    trashed: payload.trashed ?? existing?.trashed ?? false,
    remoteId: payload.id,
    uuid: payload.uuid ?? existing?.uuid,
    userId: payload.user_id ?? existing?.userId,
  };
}

function upsertPlaylistFromPayload(
  db: Database,
  payload: SunoPlaylistPayload,
  fallbackName?: string,
  fallbackCoverImage?: string
): Playlist {
  // Find existing playlist by ID
  const playlistId = payload.id ? `${payload.id}` : null;
  const existing = playlistId ? db.playlists[playlistId] : undefined;
  console.log('playlistId',playlistId)
  // Normalize and save
  const normalized = normalizeSunoPlaylist(payload, existing, fallbackName, fallbackCoverImage, db);
  db.playlists[normalized.id] = normalized;
  return normalized;
}




//РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ РУЧКИ 
/**
 * Генерирует уникальный идентификатор для сущностей (workspace, track, playlist).
 * Создает строку, состоящую из префикса, случайной строки и текущей временной метки.
 * Это гарантирует уникальность ID даже при быстром создании нескольких объектов.
 * 
 * @param prefix - Префикс для идентификатора (например, "ws" для workspace, "trk" для track)
 * @returns Уникальный строковый идентификатор
 */
function generateId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2)}_${Date.now()}`;
}

/**
 * Загружает базу данных из localStorage.
 * Если база данных отсутствует или повреждена, создает новую пустую базу.
 * Выполняет миграцию данных: добавляет недостающие поля (hasPersonas для треков),
 * инициализирует дефолтные плейлисты, если их нет.
 * Автоматически сохраняет изменения, если были внесены миграции.
 * 
 * @returns Объект базы данных с workspaces, tracks и playlists
 */
export function loadDb(): Database {
  try {
    const storageKey = getStorageKey();
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      const db = { workspaces: {}, tracks: {}, playlists: {} };
      return db;
    }
    const parsed: Database = JSON.parse(raw);
    if (!parsed.workspaces || !parsed.tracks) {
      const db = { workspaces: {}, tracks: {}, playlists: parsed.playlists || {} };
      return db;
    }

    if (!parsed.playlists) {
      parsed.playlists = {};
    }

    let needsSave = false;

    // 1) Миграция флага hasPersonas для треков
    Object.values(parsed.tracks).forEach((track) => {
      if (track.hasPersonas === undefined) {
        const personasValue = track.personas || "";
        track.hasPersonas = personasValue.trim().length > 0;
        needsSave = true;
      }
    });

    // 2) Нормализация createdAt: все даты должны быть в миллисекундах.
    // Возможный старый формат — секунды (10-значное число), что ломает сортировку.
    const normalizeCreatedAtNumber = (value: any): number | undefined => {
      if (value === null || value === undefined) return undefined;
      const num = Number(value);
      if (!Number.isFinite(num) || num <= 0) return undefined;
      // Если значение выглядит как "секунды" (меньше 1e12), домножаем на 1000.
      // Текущее время в мс ~ 1.7e12, поэтому всё, что заметно меньше, считаем секундами.
      if (num < 1e12) {
        return num * 1000;
      }
      return num;
    };

    // Треки
    Object.values(parsed.tracks).forEach((track) => {
      const normalized = normalizeCreatedAtNumber((track as any).createdAt);
      if (normalized && normalized !== (track as any).createdAt) {
        (track as any).createdAt = normalized;
        needsSave = true;
      }
    });

    // Workspace'ы
    Object.values(parsed.workspaces).forEach((ws) => {
      const createdNorm = normalizeCreatedAtNumber((ws as any).createdAt);
      if (createdNorm && createdNorm !== (ws as any).createdAt) {
        (ws as any).createdAt = createdNorm;
        needsSave = true;
      }
      const updatedNorm = normalizeCreatedAtNumber((ws as any).updatedAt);
      if (updatedNorm && updatedNorm !== (ws as any).updatedAt) {
        (ws as any).updatedAt = updatedNorm;
        needsSave = true;
      }
    });

    // Плейлисты
    Object.values(parsed.playlists).forEach((pl) => {
      const createdNorm = normalizeCreatedAtNumber((pl as any).createdAt);
      if (createdNorm && createdNorm !== (pl as any).createdAt) {
        (pl as any).createdAt = createdNorm;
        needsSave = true;
      }
      const updatedNorm = normalizeCreatedAtNumber((pl as any).updatedAt);
      if (updatedNorm && updatedNorm !== (pl as any).updatedAt) {
        (pl as any).updatedAt = updatedNorm;
        needsSave = true;
      }
    });

    // 3) Инициализация пустых плейлистов
    if (Object.keys(parsed.playlists).length === 0) {
      needsSave = true;
    }

    if (needsSave) {
      saveDb(parsed);
    }

    return parsed;
  } catch {
    const db = { workspaces: {}, tracks: {}, playlists: {} };
    return db;
  }
}


/**
 * Сохраняет базу данных в localStorage.
 * Преобразует объект базы данных в JSON строку и сохраняет под ключом STORAGE_KEY.
 * Используется после любых изменений в базе данных (создание, обновление, удаление).
 * 
 * @param db - Объект базы данных для сохранения
 */
export function saveDb(db: Database): void {
  const storageKey = getStorageKey();
  localStorage.setItem(storageKey, JSON.stringify(db));
}






// ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ  ПАПКИ 
/**
 * Создает новую рабочую область (workspace) для организации треков.
 * Генерирует уникальный ID, устанавливает имя (или "Untitled Workspace" по умолчанию),
 * инициализирует пустой массив треков и сохраняет в базу данных.
 * 
 * @param name - Название рабочей области
 * @returns Созданный объект workspace
 */
type SunoWorkspaceApiResponse = {
  success: boolean;
  data: {
    id: number;
    user_id: number;
    uuid: string;
    name: string;
    trashed: boolean;
    created_at: string;
    updated_at: string;
  };
  links?: unknown;
  message?: string;
};

/**
 * Создает новую рабочую область (workspace) при помощи Suno API.
 * Если запрос успешен — синхронизирует результат с локальной базой данных.
 *
 * @param name - Название рабочей области
 * @returns Созданный объект workspace
 */
export async function createWorkspace(name: string): Promise<Workspace> {
  const trimmedName = name.trim() || "Untitled Workspace";
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";

  try {
    const response = await axios.post<SunoWorkspaceApiResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoWorkspaces}`,
      { name: trimmedName },
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const payload = response.data?.data;
    if (!payload) {
      throw new Error("Invalid response from Suno API");
    }

    // Refresh workspaces from server after creation
    const workspaces = await fetchWorkspaces();
    return workspaces.find(w => w.name === trimmedName) || workspaces[0];
  } catch (error) {
    console.error("Failed to create workspace via Suno API", error);
    throw error;
  }
}

/**
 * Синхронизирует рабочие пространства с сервера Suno и сохраняет их локально.
 *
 * @returns Список рабочих пространств, отсортированных по дате создания
 */
export async function fetchWorkspaces(): Promise<Workspace[]> {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";

  try {
    const response = await axios.get<SunoWorkspaceListResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoWorkspaces}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const payloads = extractWorkspaceArray(response.data?.data);
    const db = loadDb();

    // Полная ресинхронизация workspaces и треков: берём только то, что пришло с сервера,
    // но сохраняем локальные треки, которые ещё генерируются (isLoading / без numericId).
    const newWorkspaces: Record<string, Workspace> = {};
    const newTracks: Record<string, Track> = {};

    // 1) Заполняем workspaces и треки из ответа сервера
    payloads.forEach((payload) => {
      const trackIds: string[] = [];
      const tracksPayload = Array.isArray(payload.tracks) ? payload.tracks : [];

      tracksPayload.forEach((tp: any) => {
        if (!tp || typeof tp !== "object") return;
        if (tp.trashed) return; // пропускаем удалённые треки

        const numericId = typeof tp.id === "number" ? tp.id : undefined;
        if (!numericId) return;
        const localTrackId = String(numericId);

        const existingTrack = db.tracks[localTrackId];

        // ВАЖНО: createdAt должен отражать реальное время создания трека в приложении.
        // Поэтому, если у трека уже есть локальный createdAt, НИКОГДА его не перезаписываем данными с сервера.
        // В качестве серверной даты используем tp.created_at (а suno_created_at только как запасной вариант).
        const createdAtRaw = tp.created_at
          ? new Date(tp.created_at).getTime()
          : tp.suno_created_at
            ? new Date(tp.suno_created_at).getTime()
            : NaN;
        const safeCreatedAt =
          existingTrack?.createdAt ??
          (Number.isFinite(createdAtRaw) ? createdAtRaw : Date.now());

        const baseParams = existingTrack?.params || {};

        const track: Track = {
          id: localTrackId,
          title: tp.title || existingTrack?.title || "Song",
          createdAt: safeCreatedAt,
          description: tp.description || existingTrack?.description,
          params: {
            ...baseParams,
            sunoTrackId: numericId,
            uuid: tp.uuid ?? baseParams.uuid,
            sunoAudioId: tp.suno_audio_id ?? baseParams.sunoAudioId,
            localAudioUrl: tp.local_audio_url ?? baseParams.localAudioUrl,
            audioUrl: tp.local_audio_url ?? baseParams.audioUrl,
            localImageUrl: tp.local_image_url ?? baseParams.localImageUrl,
            imageUrl: tp.local_image_url ?? baseParams.imageUrl,
            modelName: tp.model_name ?? baseParams.modelName,
            tags: tp.tags ?? baseParams.tags,
            workspaceRemoteId: tp.workspace_id ?? baseParams.workspaceRemoteId,
            taskId: tp.suno_task_id ?? baseParams.taskId,
            // persona binding from server
            localPersonId: tp.local_person_id ?? baseParams.localPersonId,
            sunoPersonId: tp.suno_person_id ?? baseParams.sunoPersonId,
          },
          durationSec:
            typeof tp.duration_sec === "number"
              ? tp.duration_sec
              : existingTrack?.durationSec,
          reaction: (tp.reaction as Reaction) ?? existingTrack?.reaction ?? null,
          personas: existingTrack?.personas,
          hasPersonas:
            existingTrack?.hasPersonas ?? Boolean(tp.has_personas),
          fullSongs:
            existingTrack?.fullSongs ?? Boolean(tp.full_songs ?? true),
          trashed: Boolean(tp.trashed),
        };

        newTracks[localTrackId] = track;
        trackIds.push(localTrackId);
      });

      const candidates = Object.values(db.workspaces);
      const existing =
        (payload.uuid && db.workspaces[payload.uuid]) ||
        candidates.find((ws) => ws.remoteId === payload.id) ||
        candidates.find(
          (ws) => ws.uuid && payload.uuid && ws.uuid === payload.uuid
        );

      const normalized = normalizeSunoWorkspace(payload, existing);
      normalized.trackIds = trackIds;
      newWorkspaces[normalized.id] = normalized;
    });

    // 2) Добавляем локальные "генерирующиеся" треки, которых ещё нет на сервере
    const pendingLocalTracks = Object.values(db.tracks).filter((t) => {
      if (!t || t.trashed) return false;
      const params = (t.params || {}) as Record<string, any>;
      const isLoading = params.isLoading === true;
      const sunoJob = params.sunoJob || {};
      const rawStatus = (sunoJob.status || params.status || "")
        .toString()
        .toLowerCase();
      const generatingStatuses = new Set([
        "pending",
        "processing",
        "generating",
        "queued",
      ]);
      const shouldKeepByStatus =
        isLoading || generatingStatuses.has(rawStatus);

      const numericId = getNumericTrackId(t);
      const existsOnServer =
        numericId != null &&
        Object.values(newTracks).some(
          (nt) => getNumericTrackId(nt) === numericId
        );

      // Сохраняем треки без numericId ИЛИ помеченные как генерирующиеся, которых ещё нет на сервере
      return (!numericId || shouldKeepByStatus) && !existsOnServer;
    });

    if (pendingLocalTracks.length) {
      console.log("[fetchWorkspaces] Preserving pending local tracks:", {
        count: pendingLocalTracks.length,
        ids: pendingLocalTracks.map((t) => t.id),
      });
    }

    pendingLocalTracks.forEach((track) => {
      newTracks[track.id] = track;

      // Найти workspace(ы), в которых этот трек находился, и вернуть его туда
      const oldWorkspaces = Object.values(db.workspaces).filter(
        (ws) => ws.trackIds.includes(track.id) && !ws.trashed
      );

      oldWorkspaces.forEach((oldWs) => {
        // Найти новый workspace по remoteId или uuid
        const newWs =
          Object.values(newWorkspaces).find(
            (ws) =>
              (ws.remoteId && oldWs.remoteId && ws.remoteId === oldWs.remoteId) ||
              (ws.uuid && oldWs.uuid && ws.uuid === oldWs.uuid)
          ) || null;

        if (newWs) {
          if (!newWs.trackIds.includes(track.id)) {
            newWs.trackIds.push(track.id);
          }
        }
      });
    });

    console.log("[fetchWorkspaces] Resynced workspaces and tracks:", {
      serverWorkspaces: payloads.length,
      localWorkspacesBefore: Object.keys(db.workspaces).length,
      localTracksBefore: Object.keys(db.tracks).length,
      localWorkspacesAfter: Object.keys(newWorkspaces).length,
      localTracksAfter: Object.keys(newTracks).length,
      pendingLocalTracks: pendingLocalTracks.map((t) => t.id),
    });

    db.workspaces = newWorkspaces;
    db.tracks = newTracks;
    saveDb(db);

    return listWorkspaces();
  } catch (error) {
    console.error("Failed to fetch workspaces from Suno API", error);
    throw error;
  }
}
/**
 * Получает список всех активных (не удаленных) рабочих областей.
 * Фильтрует удаленные workspace (с флагом trashed) и сортирует по дате создания
 * в порядке убывания (самые новые первыми).
 * 
 * @returns Массив активных workspace, отсортированных по дате создания
 */
export function listWorkspaces(): Workspace[] {
  const db = loadDb();
  return Object.values(db.workspaces)
    .filter(w => !w.trashed)
    .sort((a, b) => b.createdAt - a.createdAt);
}


/**
 * Перемещает рабочую область в корзину (мягкое удаление).
 * Устанавливает флаг trashed в true, что скрывает workspace из списка,
 * но не удаляет его физически из базы данных.
 * 
 * @param workspaceId - ID рабочей области для удаления
 */
export async function moveWorkspaceToTrash(workspaceId: string): Promise<void> {
  const db = loadDb();
  const ws = db.workspaces[workspaceId];
  if (!ws) return;

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  const remoteIdentifier = ws.remoteId ?? ws.uuid ?? workspaceId;

  try {
    await axios.delete(
      `${SUNO_API_BASE_URL}${API_URLS.SunoWorkspaces}/${remoteIdentifier}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    // Refresh workspaces from server after deletion
    await fetchWorkspaces();
  } catch (error) {
    console.error("Failed to move workspace to trash via Suno API", error);
    throw error;
  }
}

/**
 * Переименовывает рабочую область.
 * Обрезает пробелы в начале и конце нового имени. Если новое имя пустое,
 * оставляет старое имя без изменений.
 * 
 * @param workspaceId - ID рабочей области для переименования
 * @param name - Новое название рабочей области
 */
export async function renameWorkspace(workspaceId: string, name: string): Promise<Workspace | null> {
  const trimmed = (name || "").trim();
  if (!trimmed) return null;

  const db = loadDb();
  const ws = db.workspaces[workspaceId];
  if (!ws) return null;

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  const remoteIdentifier = ws.remoteId ?? ws.uuid ?? workspaceId;

  try {
    await axios.patch(
      `${SUNO_API_BASE_URL}${API_URLS.SunoWorkspaces}/${remoteIdentifier}`,
      { name: trimmed },
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    
    // Refresh workspaces from server after rename
    const workspaces = await fetchWorkspaces();
    return workspaces.find(w => w.id === workspaceId) || null;
  } catch (error) {
    console.error("Failed to rename workspace via Suno API", error);
    throw error;
  }
}

/**
 * Перемещает трек в указанную рабочую область.
 * Удаляет трек из всех других workspace (трек может быть только в одном workspace),
 * затем добавляет его в целевую область. Проверяет лимит в 100 треков на workspace.
 * 
 * @param trackId - ID трека для перемещения
 * @param targetWorkspaceId - ID целевой рабочей области
 * @returns true если перемещение успешно, false если трек или workspace не найдены, или превышен лимит
 */

/**
 * Перемещает трек в другой workspace на сервере.
 * После успешного перемещения обновляет данные с сервера.
 * 
 * @param trackId - ID трека для перемещения
 * @param targetWorkspaceId - ID целевого workspace (строковый UUID)
 * @returns Promise, который разрешается при успешном перемещении
 */
export async function moveTrackToWorkspace(trackId: string, targetWorkspaceId: string): Promise<boolean> {
  const db = loadDb();
  const track = db.tracks[trackId];
  const targetWs = db.workspaces[targetWorkspaceId];
  
  if (!track || !targetWs) {
    console.error('[moveTrackToWorkspace] Track or workspace not found:', {
      trackId,
      targetWorkspaceId,
      trackExists: !!track,
      workspaceExists: !!targetWs,
    });
    return false;
  }
  
  if (targetWs.trackIds.length >= 100) {
    console.warn('[moveTrackToWorkspace] Workspace has reached track limit (100)');
    return false;
  }
  
  // Get numeric IDs for server API
  const numericTrackId = getNumericTrackId(track);
  const numericWorkspaceId = targetWs.remoteId;
  
  if (!numericTrackId) {
    console.error('[moveTrackToWorkspace] Track numeric ID not found:', {
      trackId: track.id,
      trackParams: track.params,
    });
    throw new Error('Track numeric ID not found');
  }
  
  if (!numericWorkspaceId) {
    console.error('[moveTrackToWorkspace] Workspace remoteId not found:', {
      workspaceId: targetWorkspaceId,
      workspace: targetWs,
    });
    throw new Error('Workspace remoteId not found');
  }
  
  console.log('[moveTrackToWorkspace] Moving track to workspace:', {
    trackId,
    targetWorkspaceId,
    numericTrackId,
    numericWorkspaceId,
    workspaceName: targetWs.name,
  });
  
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  
  try {
    const url = `${SUNO_API_BASE_URL}${API_URLS.SunoTrackMove.replace('{track}', numericTrackId.toString())}`;
    
    // Ensure target_workspace_id is a number
    const numericTargetWorkspaceId = typeof numericWorkspaceId === 'number' 
      ? numericWorkspaceId 
      : parseInt(String(numericWorkspaceId), 10);
    
    if (isNaN(numericTargetWorkspaceId)) {
      throw new Error(`Invalid target workspace ID: ${numericWorkspaceId}`);
    }
    
    const payload = {
      target_workspace_id: numericTargetWorkspaceId,
    };
    
    console.log('[moveTrackToWorkspace] Sending request:', {
      url,
      method: 'POST',
      payload,
      payloadStringified: JSON.stringify(payload),
      numericTrackId,
      numericTargetWorkspaceId,
    });
    
    const response = await axios.post(
      url,
      payload,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log('[moveTrackToWorkspace] Server response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      dataStatus: response.data?.status,
      dataDataStatus: response.data?.data?.status,
      fullResponse: JSON.stringify(response.data, null, 2),
    });

    // Check response structure - API returns { success: true, data: { status: "ok" }, message: "" }
    const hasSuccessOk = response.data?.success === true;
    const hasStatusOk = response.data?.status === true;
    const hasDataStatusOk = response.data?.data?.status === 'ok';
    const isHttpOk = response.status === 200;
    
    console.log('[moveTrackToWorkspace] Response validation:', {
      hasSuccessOk,
      hasStatusOk,
      hasDataStatusOk,
      isHttpOk,
      responseData: response.data,
    });
    
    const isSuccess = hasSuccessOk || hasStatusOk || hasDataStatusOk || isHttpOk;
    
    if (!isSuccess) {
      console.error('[moveTrackToWorkspace] Server returned unsuccessful status:', {
        responseData: response.data,
        responseDataSuccess: response.data?.success,
        responseDataStatus: response.data?.status,
        responseDataDataStatus: response.data?.data?.status,
        httpStatus: response.status,
        message: response.data?.message,
      });
      throw new Error(response.data?.message || 'Failed to move track');
    }
    
    console.log('[moveTrackToWorkspace] Successfully moved track on server, refreshing data from server...');
    
    // Refresh workspaces and playlists from server after move to get updated track lists
    await Promise.all([
      fetchWorkspaces(),
      fetchPlaylists(),
    ]);
    
    // Verify the track was moved by checking the updated workspace
    const updatedDb = loadDb();
    const updatedTargetWs = updatedDb.workspaces[targetWorkspaceId];
    const trackInNewWs = updatedTargetWs?.trackIds.includes(trackId);
    
    // Find which workspace(s) contain this track
    const workspacesContainingTrack = Object.values(updatedDb.workspaces)
      .filter(ws => ws.trackIds.includes(trackId))
      .map(ws => ({ id: ws.id, name: ws.name }));
    
    console.log('[moveTrackToWorkspace] Verification after move:', {
      trackId,
      targetWorkspaceId,
      trackInNewWs,
      newWorkspaceTrackCount: updatedTargetWs?.trackIds.length,
      workspacesContainingTrack,
    });
    
    return true;
  } catch (error: any) {
    console.error('[moveTrackToWorkspace] Failed to move track on server', {
      error,
      errorResponse: error?.response?.data,
      errorStatus: error?.response?.status,
      errorStatusText: error?.response?.statusText,
      trackId,
      numericTrackId,
      numericWorkspaceId,
    });
    throw error;
  }
}









// ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ ПЛЕЙЛИСТЫ
const DATA_URL_REGEX = /^data:([^;]+);base64,(.+)$/;
const ALLOWED_IMAGE_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);
const SUNO_MEDIA_BASE_URL = `${SUNO_API_STORAGE_URL.replace(/\/+$/, '')}/`;

const isDataUrl = (value: unknown): value is string => {
  return typeof value === 'string' && DATA_URL_REGEX.test(value);
};

const parseDataUrl = (dataUrl: string): { mimeType: string; base64: string } => {
  const match = DATA_URL_REGEX.exec(dataUrl);
  if (!match) {
    throw new Error('Invalid data URL');
  }
  const mimeType = match[1];
  if (!ALLOWED_IMAGE_MIME_TYPES.has(mimeType)) {
    throw new Error(`Unsupported image mime type: ${mimeType}`);
  }
  const base64Data = match[2];
  return { mimeType, base64: base64Data };
};

const resolveSunoAssetUrl = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  if (typeof value !== "string") return undefined;
  // Специальный формат для однотонных/градиентных обложек, не преобразуем в URL
  if (value.startsWith("gradient:")) return value;
  if (value.startsWith("data:")) return value;
  if (/^https?:\/\//i.test(value)) return value;
  const trimmed = value.replace(/^\/+/, "");
  return `${SUNO_MEDIA_BASE_URL}${trimmed}`;
};

const normalizePersonaId = (value: string | number | null | undefined): string | null => {
  if (value === undefined || value === null) return null;
  const stringValue = value.toString().trim();
  return stringValue.length ? stringValue : null;
};

type PersonaIdentifiers = {
  taskId?: string;
  audioId?: string;
  personaId?: string | null;
};

export function extractTrackPersonaIdentifiers(track: Track): PersonaIdentifiers {
  if (!track?.params) {
    return {};
  }

  const params = track.params as Record<string, any>;
  const sunoJob = params.sunoJob ?? {};
  const sunoResult = params.sunoResult;
  const slotIndexRaw = params.slotIndex;
  const slotIndex = Number.isFinite(Number(slotIndexRaw)) ? Number(slotIndexRaw) : 0;

  const rawTaskId = sunoJob.taskId || sunoJob.task_id || sunoJob.taskID || params.taskId;
  let rawAudioId =
    params.audioId ||
    params.audio_id ||
    params.sunoAudioId ||
    params.sunoTrackId ||
    params.trackId;
  const rawPersonaId =
    sunoJob.personaId ||
    sunoJob.persona_id ||
    sunoJob.id ||
    params.personaId ||
    params.persona_id ||
    sunoJob.jobId ||
    rawTaskId;

  if (!rawAudioId && Array.isArray(params.tracks) && params.tracks.length) {
    const candidate = params.tracks[slotIndex] || params.tracks[0];
    rawAudioId =
      candidate?.suno_audio_id ||
      candidate?.sunoAudioId ||
      candidate?.audio_id ||
      candidate?.audioId ||
      candidate?.id;
  }

  if (!rawAudioId && sunoResult?.data?.tracks) {
    const rawTracks = sunoResult.data.tracks;
    const trackList = Array.isArray(rawTracks) ? rawTracks : Object.values(rawTracks as Record<string, any>);
    if (trackList.length) {
      const candidate = trackList[slotIndex] || trackList[0];
      rawAudioId =
        candidate?.suno_audio_id ||
        candidate?.sunoAudioId ||
        candidate?.audio_id ||
        candidate?.audioId ||
        candidate?.id;
    }
  }

  const taskId = typeof rawTaskId === 'string' && rawTaskId.trim() ? rawTaskId.trim() : undefined;
  const audioId = typeof rawAudioId === 'string' && rawAudioId.trim() ? rawAudioId.trim() : undefined;
  const personaId =
    typeof rawPersonaId === 'string' && rawPersonaId.trim() ? rawPersonaId.trim() : null;

  return { taskId, audioId, personaId };
}

export async function ensureTrackPersonaIdentifiers(trackId: string): Promise<PersonaIdentifiers> {
  const db = loadDb();
  const track = db.tracks[trackId];
  if (!track) {
    throw new Error('Track not found');
  }

  let identifiers = extractTrackPersonaIdentifiers(track);
  if (identifiers.audioId || !identifiers.taskId) {
    return identifiers;
  }

  try {
    const record = await getSunoGenerationRecord(identifiers.taskId);
    if (record?.links || record?.data?.links) {
      console.debug('[Persona] record-info links snapshot', {
        trackId,
        taskId: identifiers.taskId,
        links: record.links ?? record.data?.links,
      });
    }
    const recordTracks = record?.data?.tracks;
    if (recordTracks) {
      const trackList = Array.isArray(recordTracks)
        ? recordTracks
        : Object.values(recordTracks as Record<string, any>);
      if (trackList.length) {
        const slotIndexRaw = track.params?.slotIndex;
        const slotIndex = Number.isFinite(Number(slotIndexRaw)) ? Number(slotIndexRaw) : 0;
        const candidate = trackList[slotIndex] || trackList[0];
        console.debug('[Persona] record-info track', {
          trackId,
          slotIndex,
          candidateId: candidate?.id,
          candidateAudioId: candidate?.audio_id || candidate?.audioId,
          candidateAudioUrl: candidate?.audio_url || candidate?.stream_audio_url,
        });

        const result = updateTrack(trackId, (target) => {
          const nextParams: Record<string, any> = {
            ...(target.params || {}),
            tracks: trackList,
          };

          const localAudioUrl =
            candidate?.local_audio_url ||
            candidate?.localAudioUrl ||
            candidate?.media?.local_audio_url ||
            candidate?.media?.localAudioUrl ||
            null;

          console.debug('[Persona] resolved audio sources', {
            trackId,
            taskId: identifiers.taskId,
            candidateId: candidate?.id,
            suno_audio_id: candidate?.suno_audio_id,
            local_audio_url: candidate?.local_audio_url,
            audio_url: candidate?.audio_url,
          });

          if (localAudioUrl) {
            nextParams.localAudioUrl = localAudioUrl;
          } else {
            delete nextParams.localAudioUrl;
          }

          nextParams.audioUrl = localAudioUrl || null;
          if (!localAudioUrl) {
            delete nextParams.audioUrl;
          }

          const sunoAudioId = candidate?.suno_audio_id || candidate?.sunoAudioId || null;
          if (sunoAudioId) {
            nextParams.sunoAudioId = sunoAudioId;
          }

          const localImageUrl =
            candidate?.local_image_url ||
            candidate?.localImageUrl ||
            candidate?.media?.local_image_url ||
            candidate?.media?.localImageUrl ||
            null;
          const remoteImageUrl = candidate?.image_url || candidate?.imageUrl || null;
          const resolvedImageUrl = localImageUrl || remoteImageUrl || null;

          console.debug('[Persona] resolved imagery sources', {
            trackId,
            candidateId: candidate?.id,
            local_image_url: candidate?.local_image_url,
            image_url: candidate?.image_url,
            resolvedImageUrl,
          });

          if (localImageUrl) {
            nextParams.localImageUrl = localImageUrl;
          } else {
            delete nextParams.localImageUrl;
          }

          if (resolvedImageUrl) {
            nextParams.imageUrl = resolvedImageUrl;
          } else {
            delete nextParams.imageUrl;
          }

          const candidateAudioId =
            candidate?.suno_audio_id ||
            candidate?.sunoAudioId ||
            candidate?.audio_id ||
            candidate?.audioId ||
            candidate?.id;
          if (candidateAudioId) {
            nextParams.audioId = candidateAudioId;
          }
          if (candidate?.id) {
            nextParams.sunoTrackId = candidate.id;
          }
          if (candidate?.model_name) {
            nextParams.modelName = candidate.model_name;
          }
          if (candidate?.tags) {
            nextParams.tags = candidate.tags;
          }

          target.params = nextParams;
          if (typeof candidate?.duration === 'number') {
            target.durationSec = candidate.duration;
          }
          if (candidate?.prompt && !target.description) {
            target.description = candidate.prompt;
          }
        });

        const refreshed = result || loadDb().tracks[trackId];
        if (refreshed) {
          identifiers = extractTrackPersonaIdentifiers(refreshed);
        }
      }
    }
  } catch (error) {
    console.warn('Failed to refresh track persona identifiers', trackId, error);
  }

  console.debug('[Persona] ensureTrackPersonaIdentifiers', {
    trackId,
    identifiers,
  });

  return identifiers;
}

export async function fetchPersonasFromServer(): Promise<PersonaSummary[]> {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  try {
    const response = await axios.get<SunoPersonsListResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPersons}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const persons = response.data?.data?.persons || [];
    const results: PersonaSummary[] = persons
      .filter((p) => !p.trashed)
      .map((p) => {
        const persona: PersonaSummary = {
          id: p.id,
          name: p.name || "",
          personaId: p.suno_person_id || null,
          trashed: p.trashed,
        };
        if (p.description) {
          persona.description = p.description;
        }
        const resolvedImageUrl = resolveSunoAssetUrl(p.image_url);
        if (resolvedImageUrl) {
          persona.imageUrl = resolvedImageUrl;
        }
        // trackId, taskId, audioId are not available from server API
        // They are only available when personas are extracted from local tracks
        return persona;
      });

    console.log('[Personas] fetchPersonasFromServer - received from server:', {
      total: persons.length,
      active: results.length,
      persons: persons.map(p => ({
        id: p.id,
        suno_person_id: p.suno_person_id,
        name: p.name,
        description: p.description,
        image_url: p.image_url,
        resolved_image_url: resolveSunoAssetUrl(p.image_url),
        trashed: p.trashed,
        tracks_count: p.tracks?.length || 0,
        tracks: p.tracks,
      })),
    });

    return results;
  } catch (error) {
    console.error('[Personas] Failed to fetch personas from server', error);
    throw error;
  }
}

export function listPersonas(): PersonaSummary[] {
  const db = loadDb();
  const results: PersonaSummary[] = [];
  Object.values(db.tracks).forEach((track) => {
    if (!track || track.trashed) return;
    const name = (track.personas || '').trim();
    if (!name) return;
    const params = (track.params || {}) as Record<string, any>;
    const identifiers = extractTrackPersonaIdentifiers(track);
    const description = (params.personaDescription || '').toString().trim();
    const persona = {
      trackId: track.id,
      name,
      description: description || undefined,
      personaId: identifiers.personaId,
      taskId: identifiers.taskId,
      audioId: identifiers.audioId,
    };
    results.push(persona);
    console.log('[Personas] listPersonas - persona from track:', {
      persona,
      trackParams: {
        sunoJob: params.sunoJob,
        status: params.status,
        personaId: params.personaId,
        selectedPersonaId: params.selectedPersonaId,
        selectedPersonaName: params.selectedPersonaName,
        selectedPersonaDescription: params.selectedPersonaDescription,
        selectedPersonaKey: params.selectedPersonaKey,
      },
    });
  });
  console.log('[Personas] listPersonas - total personas found:', results.length, results);
  return results;
}

const applyPersonaMutationToTracks = (
  personaId: string,
  updater: (track: Track, params: Record<string, any>) => void
): boolean => {
  const db = loadDb();
  let hasChanges = false;
  Object.values(db.tracks).forEach((track) => {
    if (!track) return;
    const identifiers = extractTrackPersonaIdentifiers(track);
    const identifierId = normalizePersonaId(identifiers.personaId);
    if (!identifierId || identifierId !== personaId) return;

    const params = (track.params || {}) as Record<string, any>;
    updater(track, params);
    track.params = params;
    hasChanges = true;
  });

  if (hasChanges) {
  saveDb(db);
  }
  return hasChanges;
};

export async function updatePersonaMetadata(
  personaId: number,
  payload: { name?: string; description?: string; image?: string | null }
): Promise<void> {
  if (!Number.isFinite(personaId) || personaId <= 0) {
    throw new Error("Invalid persona identifier: must be a positive number");
}

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const body: { name?: string | null; description?: string | null; image?: string | null } = {};
  if (payload.name !== undefined) {
    body.name = payload.name ?? "";
  }
  if (payload.description !== undefined) {
    body.description = payload.description ?? "";
  }
  if (payload.image !== undefined) {
    body.image = payload.image ?? null;
  }

  console.log('[Persona] updatePersonaMetadata - sending PATCH request', {
    personaId: personaId,
    body: {
      ...body,
      image: body.image ? `[base64 data, length: ${body.image.length}]` : null,
    },
  });

  try {
    const response = await axios.patch(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPersons}/${personaId}`,
      body,
      { headers }
    );
    console.log('[Persona] updatePersonaMetadata - success', {
      personaId: personaId,
      response: response.data,
    });
  } catch (error) {
    console.error("Failed to update persona metadata", error);
    throw error;
  }

  // Get suno_person_id from server to find tracks
  let sunoPersonId: string | null = null;
  try {
    const personas = await fetchPersonasFromServer();
    const persona = personas.find(p => p.id === personaId);
    if (persona && persona.personaId) {
      sunoPersonId = persona.personaId;
    }
  } catch (error) {
    console.warn("Failed to fetch personas to get suno_person_id", error);
  }

  const searchId = sunoPersonId;
  let hasChanges = false;
  if (searchId) {
    hasChanges = applyPersonaMutationToTracks(searchId, (track, params) => {
      if (payload.name !== undefined) {
        const trimmedName = (payload.name || "").trim();
        track.personas = trimmedName;
        track.hasPersonas = trimmedName.length > 0;
        if (trimmedName) {
          params.selectedPersonaName = trimmedName;
        } else {
          delete params.selectedPersonaName;
        }
        if (params.personaSelection && typeof params.personaSelection === "object") {
          params.personaSelection = {
            ...params.personaSelection,
            name: trimmedName,
          };
        }
      }

      if (payload.description !== undefined) {
        const trimmedDescription = (payload.description || "").trim();
        if (trimmedDescription) {
          params.personaDescription = trimmedDescription;
          params.selectedPersonaDescription = trimmedDescription;
        } else {
          delete params.personaDescription;
          delete params.selectedPersonaDescription;
        }
        if (params.personaSelection && typeof params.personaSelection === "object") {
          params.personaSelection = {
            ...params.personaSelection,
            description: trimmedDescription,
          };
        }
      }
    });
  }

  if (hasChanges && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("personaUpdated", {
        detail: { personaId: sunoPersonId || personaId.toString() },
      })
    );
  }
}

export async function deletePersonaRecord(personaId: number): Promise<void> {
  if (!Number.isFinite(personaId) || personaId <= 0) {
    throw new Error("Invalid persona identifier: must be a positive number");
  }

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  // Get suno_person_id from server to find tracks before deletion
  let sunoPersonId: string | null = null;
  try {
    const personas = await fetchPersonasFromServer();
    const persona = personas.find(p => p.id === personaId);
    if (persona && persona.personaId) {
      sunoPersonId = persona.personaId;
    }
  } catch (error) {
    console.warn("Failed to fetch personas to get suno_person_id", error);
  }

  console.log('[Persona] deletePersonaRecord - sending DELETE request', {
    personaId: personaId,
    suno_person_id: sunoPersonId,
  });

  try {
    const response = await axios.delete(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPersons}/${personaId}`,
      { headers }
    );
    console.log('[Persona] deletePersonaRecord - success', {
      personaId: personaId,
      response: response.data,
    });
  } catch (error) {
    console.error("Failed to delete persona", error);
    throw error;
  }

  const searchId = sunoPersonId;
  let hasChanges = false;
  if (searchId) {
    hasChanges = applyPersonaMutationToTracks(searchId, (track, params) => {
      track.personas = "";
      track.hasPersonas = false;
      delete params.personaDescription;
      delete params.selectedPersonaId;
      delete params.selectedPersonaName;
      delete params.selectedPersonaDescription;
      delete params.personaSelection;
    });
  }

  if (hasChanges && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("personaDeleted", {
        detail: { personaId: sunoPersonId || personaId.toString() },
      })
    );
  }
}

/**
 * Инициализирует дефолтный плейлист "Liked Songs" (Понравившиеся песни).
 * Создается автоматически при первой загрузке базы данных, если его еще нет.
 * Этот плейлист используется для автоматического добавления треков с реакцией "like".
 * 
 * @param db - Объект базы данных для инициализации
 */
function initializeDefaultPlaylists(db: Database): void {
  const likedSongsId = 'playlist_liked_songs';
  if (!db.playlists[likedSongsId]) {
    db.playlists[likedSongsId] = {
      id: likedSongsId,
      name: 'Liked Songs',
      createdAt: Date.now(),
      trackIds: [],
    };
    saveDb(db);
  }
}
/**
 * Создает новый плейлист при помощи Suno API и синхронизирует его локально.
 * 
 * @param name - Название плейлиста
 * @param coverImage - Опциональный base64/URL изображения обложки
 * @returns Созданный плейлист
 */
export async function createPlaylist(name: string, coverImage?: string | null): Promise<Playlist> {
  const trimmedName = (name || "").trim() || "Untitled";
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  const gradientKey =
    typeof coverImage === 'string' && coverImage.startsWith('gradient:')
      ? coverImage
      : null;

  try {
    const headers: Record<string, string> = {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    };

    headers["Content-Type"] = "application/json";

    const requestBody: { name: string; cover_image?: string | null } = {
      name: trimmedName,
    };

    // Не отправляем градиент на сервер — для бэкенда это означает "картинка не задана"
    if (coverImage !== undefined && !gradientKey) {
      if (coverImage && isDataUrl(coverImage)) {
        parseDataUrl(coverImage);
        requestBody.cover_image = coverImage;
      } else {
        requestBody.cover_image = resolveSunoAssetUrl(coverImage) ?? null;
      }
    }

    const response = await axios.post<SunoPlaylistApiResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPlaylists}`,
      requestBody,
      {
        headers,
      }
    );

    const responseData = response.data?.data;
    if (!responseData) {
      throw new Error("Invalid response from Suno API");
    }

    // Refresh playlists from server after creation
    const playlists = await fetchPlaylists();
    const created = playlists.find(p => p.name === trimmedName) || playlists[0];

    // Локально сохраняем градиент, если он был выбран
    if (created && gradientKey) {
      const db = loadDb();
      const localPlaylist = db.playlists[created.id];
      if (localPlaylist) {
        localPlaylist.coverImage = gradientKey;
        saveDb(db);
      }
      const updatedDb = loadDb();
      return updatedDb.playlists[created.id] || created;
    }

    return created;
  } catch (error) {
    console.error("Failed to create playlist via Suno API", error);
    throw error;
  }
}

/**
 * Синхронизирует плейлисты пользователя с Suno API и обновляет локальную базу.
 *
 * @returns Список актуальных плейлистов
 */
export async function fetchPlaylists(): Promise<Playlist[]> {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";

  try {
    const response = await axios.get<SunoPlaylistListResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPlaylists}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const payloads = extractPlaylistArray(response.data?.data);
    const db = loadDb();

    // Полная ресинхронизация: локальные плейлисты = ровно то, что вернул сервер
    const newPlaylists: Record<string, Playlist> = {};

    // Создаём/обновляем плейлисты только из ответа сервера
    payloads.forEach((payload) => {
      if (payload.trashed) return; // пропускаем удалённые на сервере
      const normalized = normalizeSunoPlaylist(payload, undefined, undefined, undefined, db);
      newPlaylists[normalized.id] = normalized;
    });

    console.log("[fetchPlaylists] Resynced playlists:", {
      serverCount: payloads.length,
      localCountBefore: Object.keys(db.playlists).length,
      localCountAfter: Object.keys(newPlaylists).length,
    });

    db.playlists = newPlaylists;
    saveDb(db);

    return listPlaylists();
  } catch (error) {
    console.error("Failed to fetch playlists from Suno API", error);
    throw error;
  }
}

/**
 * Получает список всех активных (не удаленных) плейлистов.
 * Фильтрует удаленные плейлисты (с флагом trashed) и сортирует по дате создания
 * в порядке убывания (самые новые первыми).
 * 
 * @returns Массив активных плейлистов, отсортированных по дате создания
 */
export function listPlaylists(): Playlist[] {
  const db = loadDb();
  return Object.values(db.playlists)
    .filter(p => !p.trashed)
    .sort((a, b) => b.createdAt - a.createdAt);
}


/**
 * Удаляет плейлист (мягкое удаление).
 * Устанавливает флаг trashed в true, что скрывает плейлист из списка,
 * но не удаляет его физически из базы данных. Треки остаются в базе.
 * 
 * @param playlistId - ID плейлиста для удаления
 */
export async function deletePlaylist(playlistId: string): Promise<void> {
  const db = loadDb();
  const playlist = db.playlists[playlistId];
  if (!playlist) return;

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  const remoteIdentifier = playlist.remoteId ?? playlist.uuid ?? playlistId;

  try {
    await axios.delete(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPlaylists}/${remoteIdentifier}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
  } catch (error) {
    console.error("Failed to delete playlist via Suno API", error);
    throw error;
  }

  // Refresh playlists from server after deletion
  await fetchPlaylists();
}

/**
 * Обновляет данные плейлиста (название и/или обложку).
 * Обновляет только те поля, которые были переданы (не undefined).
 * Если новое имя пустое, оставляет старое имя без изменений.
 * 
 * @param playlistId - ID плейлиста для обновления
 * @param name - Новое название плейлиста (опционально)
 * @param coverImage - Новый URL обложки плейлиста (опционально)
 */
export async function updatePlaylist(playlistId: string, name?: string, coverImage?: string | null): Promise<Playlist | null> {
  const db = loadDb();
  const playlist = db.playlists[playlistId];
  if (!playlist) return null;

  const gradientKey =
    typeof coverImage === 'string' && coverImage.startsWith('gradient:')
      ? coverImage
      : null;

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  const remoteIdentifier = playlist.remoteId ?? playlist.uuid ?? playlistId;

  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const jsonPayload: { name?: string; cover_image?: string | null } = {};
  let shouldSendRequest = false;

  if (name !== undefined) {
    jsonPayload.name = (name || '').trim() || playlist.name;
    shouldSendRequest = true;
  }

  if (coverImage !== undefined) {
    // Не отправляем градиент на сервер — для бэкенда это означает "картинка не задана"
    if (!gradientKey) {
      if (coverImage && isDataUrl(coverImage)) {
        parseDataUrl(coverImage);
        jsonPayload.cover_image = coverImage;
      } else {
        jsonPayload.cover_image = resolveSunoAssetUrl(coverImage) ?? null;
      }
      shouldSendRequest = true;
    }
  }

  if (shouldSendRequest) {
    headers["Content-Type"] = "application/json";
  }

  try {
    if (shouldSendRequest) {
      await axios.patch(
        `${SUNO_API_BASE_URL}${API_URLS.SunoPlaylists}/${remoteIdentifier}`,
        jsonPayload,
        {
          headers,
        }
      );
    }
    
    // Refresh playlists from server after update
    const playlists = await fetchPlaylists();
    const updated = playlists.find(p => p.id === playlistId) || null;

    // Если выбран градиент — сохраняем его только локально
    if (gradientKey && updated) {
      const localDb = loadDb();
      const localPlaylist = localDb.playlists[playlistId];
      if (localPlaylist) {
        localPlaylist.coverImage = gradientKey;
        saveDb(localDb);
      }
      const refreshed = loadDb();
      return refreshed.playlists[playlistId] || updated;
    }

    return updated;
  } catch (error) {
    console.error("Failed to update playlist via Suno API", error);
    throw error;
  }
}

export async function startSunoGeneration(
  request: SunoGenerateRequest
): Promise<{ jobId: string; taskId: string; status: string }> {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";

  try {
    const response = await axios.post<SunoGenerateResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoGenerate}`,
      request,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const payload = response.data?.data;
    if (!payload?.jobId || !payload?.taskId || !payload?.status) {
      throw new Error(response.data?.message || "Invalid response from Suno generation API");
    }

    return {
      jobId: payload.jobId,
      taskId: payload.taskId,
      status: payload.status,
    };
  } catch (error) {
    console.error("Failed to start Suno generation", error);
    throw error;
  }
}

export async function getSunoGenerationRecord(taskId: string): Promise<SunoGenerationRecordResponse | null> {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  try {
    const response = await axios.get<SunoGenerationRecordResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoGenerateRecordInfo}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        params: { taskId },
      }
    );
    
    // Log detailed tracks data from server response BEFORE any processing
    const tracksData = response.data?.data?.tracks;
    if (tracksData) {
      const tracksArray = Array.isArray(tracksData) ? tracksData : Object.values(tracksData);
      console.log("[SunoGenerationRecord] RAW tracks data DIRECTLY from server (BEFORE processing) for taskId:", taskId, {
        tracksCount: tracksArray.length,
        tracks: tracksArray.map((t: any, idx: number) => ({
          index: idx,
          id: t?.id,
          uuid: t?.uuid,
          suno_audio_id: t?.suno_audio_id,
          title: t?.title,
          local_audio_url: t?.local_audio_url,
          localAudioUrl: t?.localAudioUrl,
          media_local_audio_url: t?.media?.local_audio_url,
          media_localAudioUrl: t?.media?.localAudioUrl,
          fullTrackData: t ? JSON.parse(JSON.stringify(t)) : null
        }))
      });
    }
    
    console.log("[SunoGenerationRecord] Full response snapshot for taskId:", taskId, response.data);
    if (!response.data) {
      return null;
    }
    return response.data;
  } catch (error) {
    console.error("Failed to fetch Suno generation record", taskId, error);
    throw error;
  }
}

/**
 * Публичное получение информации о треке по UUID для страницы Song.
 */
export async function getPublicTrack(uuid: string): Promise<any | null> {
  try {
    const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? null;
    const response = await axios.get(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPublicTrack.replace("{uuid}", uuid)}`,
      {
        headers: {
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        // ВАЖНО: не бросать ошибку по коду статуса, чтобы можно было обработать 404 с полезными данными
        validateStatus: () => true,
      }
    );

    // Нормальные 2xx ответы
    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }

    // На сервере бывает кейс, когда приходит 404, но внутри data есть валидный объект трека.
    if (response.status === 404 && response.data && (response.data as any).data) {
      console.warn("[getPublicTrack] Received 404 with track data, using response.data anyway", {
        status: response.status,
        dataKeys: Object.keys(response.data as any),
      });
      return response.data;
    }

    console.error("[getPublicTrack] Unexpected status when fetching public track:", {
      status: response.status,
      data: response.data,
    });
    return null;
  } catch (error) {
    console.error("[getPublicTrack] Failed to fetch public track:", error);
    return null;
  }
}

type SunoPersonaJobPayload = {
  jobId?: string;
  taskId?: string;
  status?: string;
  personaId?: string | null;
};

type SunoPersonaResponse = {
  success?: boolean;
  data?: SunoPersonaJobPayload;
  message?: string;
  links?: unknown;
};

export async function generateTrackPersona(
  trackId: string,
  name: string,
  description: string,
  image?: string | null
): Promise<SunoPersonaJobPayload> {
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  if (!trimmedName) {
    throw new Error('Persona name is required');
  }
  if (!trimmedDescription) {
    throw new Error('Persona description is required');
  }

  const db = loadDb();
  const track = db.tracks[trackId];
  if (!track) {
    throw new Error('Track not found');
  }

  const identifiers = extractTrackPersonaIdentifiers(track);
  if (!identifiers.taskId || !identifiers.audioId) {
    throw new Error('Track is missing required identifiers for persona generation');
  }

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";

  try {
    // Новое API: Create a new AI person
    // POST /suno/persons
    const requestBody: any = {
      taskId: identifiers.taskId,
      audioId: identifiers.audioId,
      name: trimmedName,
      description: trimmedDescription,
    };

    if (image) {
      requestBody.image = image;
    }

    const response = await axios.post<SunoPersonaResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPersons}`,
      requestBody,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    const payload = response.data?.data;
    if (!payload) {
      throw new Error(response.data?.message || 'Invalid response from Suno persona API');
    }

    console.log('[Persona] generateTrackPersona - API response:', {
      payload,
      fullResponse: response.data,
      sentData: {
        taskId: identifiers.taskId,
        audioId: identifiers.audioId,
        name: trimmedName,
        description: trimmedDescription,
      },
    });

    const personaId = payload.personaId || payload.taskId || payload.jobId || identifiers.personaId || null;
    
    console.log('[Persona] generateTrackPersona - extracted personaId:', personaId);

    // If image is provided, update persona with image after creation
    if (image && personaId) {
      try {
        // Get personas list to find numeric id by suno_person_id
        const personas = await fetchPersonasFromServer();
        const persona = personas.find(p => p.personaId === personaId);
        if (persona && persona.id) {
          console.log('[Persona] Updating persona with image after creation', {
            personaId: persona.id,
            suno_person_id: personaId,
          });
          await updatePersonaMetadata(persona.id, {
            name: trimmedName,
            description: trimmedDescription,
            image: image,
          });
        } else {
          console.warn('[Persona] Could not find persona to update with image', {
            personaId,
            personas: personas.map(p => ({ id: p.id, personaId: p.personaId })),
          });
        }
      } catch (error) {
        console.error('[Persona] Failed to update persona with image after creation', error);
        // Don't throw - persona was created successfully, image update is optional
      }
    }

    const nextParams: Record<string, any> = {
      ...(track.params || {}),
      personaJob: {
        ...payload,
        personaId,
      },
      personaRequest: {
        name: trimmedName,
        description: trimmedDescription,
      },
    };
    if (trimmedDescription) {
      nextParams.personaDescription = trimmedDescription;
    }
    if (personaId) {
      nextParams.personaId = personaId;
    }

    track.params = nextParams;

    const personaLabel = trimmedName || track.personas || trimmedDescription;
    track.personas = personaLabel;
    track.hasPersonas = Boolean((personaLabel || '').trim());

  saveDb(db);

    // Check if persona appears in server list and has tracks
    if (personaId) {
      try {
        // Wait a bit for server to process
        await new Promise(resolve => setTimeout(resolve, 1000));
        const personas = await fetchPersonasFromServer();
        const createdPersona = personas.find(p => p.personaId === personaId);
        if (createdPersona) {
          console.log('[Persona] generateTrackPersona - persona found in server list:', {
            personaId: createdPersona.personaId,
            id: createdPersona.id,
            name: createdPersona.name,
            // Note: tracks are not in PersonaSummary, they're in the server response
          });
        } else {
          console.warn('[Persona] generateTrackPersona - persona not found in server list yet', {
            personaId,
            allPersonas: personas.map(p => ({ id: p.id, personaId: p.personaId, name: p.name })),
          });
        }
      } catch (error) {
        console.warn('[Persona] generateTrackPersona - failed to check persona in server list', error);
      }
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('personaCreated', {
          detail: {
            trackId,
            persona: trimmedName,
            personaId,
          },
        })
      );
    }

    return payload;
  } catch (error) {
    console.error('Failed to generate persona via Suno API', trackId, error);
    throw error;
  }
}

export function updateTrack(trackId: string, updater: (track: Track) => void): Track | null {
  const db = loadDb();
  const track = db.tracks[trackId];
  if (!track) return null;
  updater(track);
  saveDb(db);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('trackUpdated', { detail: { trackId } }));
  }
  return track;
}

const downloadFile = async (url: string, filename: string): Promise<void> => {
  // For cross-origin URLs, fetch as blob first (more reliable)
  // For same-origin URLs, direct download works fine
  let isCrossOrigin = false;
  try {
    const urlObj = new URL(url, window.location.href);
    isCrossOrigin = urlObj.origin !== window.location.origin;
  } catch (e) {
    // If URL parsing fails, assume it's cross-origin and try blob download
    isCrossOrigin = true;
  }
  
  if (isCrossOrigin) {
    // Try fetching as blob first (works for cross-origin with CORS)
    try {
      console.log('[Download] Attempting blob download for cross-origin URL', { url });
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText} (${response.status})`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      console.log('[Download] Blob download successful', { filename });
      return;
    } catch (fetchError) {
      console.warn('[Download] Blob download failed, trying direct download', fetchError);
      // Fall through to direct download attempt
    }
  }
  
  // Try direct download (works for same-origin or if server allows it)
  try {
    console.log('[Download] Attempting direct download', { url });
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    console.log('[Download] Direct download initiated', { filename });
  } catch (error) {
    console.error('[Download] Both download methods failed', error);
    throw new Error(`Failed to download file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export async function downloadTrackAsMp3(track: Track): Promise<void> {
  const params = track.params as Record<string, any> | undefined;
  const localAudioUrl = params?.localAudioUrl;
  
  if (!localAudioUrl) {
    throw new Error('Track audio URL not available');
  }

  console.log('[Download] Starting MP3 download', {
    trackId: track.id,
    trackTitle: track.title,
    audioUrl: localAudioUrl,
  });

  try {
    // Fetch file as blob to handle CORS properly
    const response = await fetch(localAudioUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch audio file: ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const filename = `${track.title || 'track'}.mp3`.replace(/[^a-z0-9.-]/gi, '_');
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    
    console.log('[Download] MP3 download initiated', { filename });
  } catch (error) {
    console.error('[Download] Failed to download MP3', error);
    throw error;
  }
}

type SunoWavGenerateResponse = {
  status: boolean;
  data: {
    jobId: number;
    taskId: string;
    status: string;
  };
  message: string;
};

type SunoWavRecordInfoResponse = {
  success: boolean;
  data: {
    audioWavUrl?: string;
    job?: {
      id: number;
      type: string;
      status: string;
      task_id: string;
      parent_music_id: string | null;
      [key: string]: any;
    };
    [key: string]: any;
  } | string; // Can be string (direct URL) or object with audioWavUrl
  links?: unknown;
  message?: string;
};

export async function downloadTrackAsWav(track: Track): Promise<void> {
  const params = track.params as Record<string, any> | undefined;
  const identifiers = extractTrackPersonaIdentifiers(track);
  const taskId = identifiers.taskId;
  const audioId = identifiers.audioId;

  if (!taskId) {
    throw new Error('Track task ID not available');
  }
  if (!audioId) {
    throw new Error('Track audio ID not available');
  }

  console.log('[Download] Starting WAV conversion', {
    trackId: track.id,
    trackTitle: track.title,
    taskId,
    audioId,
  });

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";

  try {
    // Step 1: Start WAV conversion
    const generateResponse = await axios.post<SunoWavGenerateResponse>(
      `${SUNO_API_BASE_URL}${API_URLS.SunoWavGenerate}`,
      {
        taskId,
        audioId,
      },
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log('[Download] WAV conversion started', {
      response: generateResponse.data,
      wavTaskId: generateResponse.data?.data?.taskId || taskId,
    });

    const wavTaskId = generateResponse.data?.data?.taskId || taskId;

    // Step 2: Poll for WAV URL until ready
    const maxAttempts = 30; // 30 attempts = 30 seconds max
    const pollInterval = 1000; // 1 second
    let wavUrl: string | null = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      try {
        const statusResponse = await axios.get<SunoWavRecordInfoResponse>(
          `${SUNO_API_BASE_URL}${API_URLS.SunoWavRecordInfo}`,
          {
            params: { taskId: wavTaskId },
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('[Download] WAV status check', {
          attempt: attempt + 1,
          response: statusResponse.data,
        });

        const responseData = statusResponse.data?.data;
        
        // Check job status first (only if data is an object)
        if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
          const dataObj = responseData.data as Record<string, any>;
          const jobStatus = dataObj.job?.status;
          if (jobStatus && jobStatus !== 'pending' && jobStatus !== 'processing') {
            // Job is completed or failed
            if (jobStatus === 'failed' || jobStatus === 'error') {
              throw new Error(`WAV conversion failed with status: ${jobStatus}`);
            }
          }

          // Try to get WAV URL from different possible locations
          // Primary location: response.data.data.audioWavUrl
          if (dataObj.audioWavUrl && typeof dataObj.audioWavUrl === 'string' && dataObj.audioWavUrl.trim()) {
            wavUrl = dataObj.audioWavUrl.trim();
            console.log('[Download] WAV URL found in audioWavUrl, breaking loop', { wavUrl, attempt: attempt + 1 });
            break;
          } else {
            
          }
          // Also check other possible fields
          const altUrl = dataObj.url || dataObj.wav_url || dataObj.wavUrl || dataObj.download_url;
          console.log('[Download] Step 13 - altUrl:', altUrl);
          if (altUrl && typeof altUrl === 'string' && altUrl.trim()) {
            wavUrl = altUrl.trim();
            console.log('[Download] WAV URL found in alternative field, breaking loop', { wavUrl, attempt: attempt + 1 });
            break;
          }
        }
        // Check if data is a string (direct URL)
        else if (typeof responseData === 'string' && responseData.trim()) {
          wavUrl = responseData.trim();
          console.log('[Download] WAV URL found as direct string, breaking loop', { wavUrl, attempt: attempt + 1 });
          break;
        }
        
        // Check links field (only if we haven't found URL yet)
        if (!wavUrl && statusResponse.data?.links) {
          if (typeof statusResponse.data.links === 'string' && statusResponse.data.links.trim()) {
            wavUrl = statusResponse.data.links.trim();
            console.log('[Download] WAV URL found in links string, breaking loop', { wavUrl, attempt: attempt + 1 });
            break;
          }
          else if (typeof statusResponse.data.links === 'object') {
            const linksObj = statusResponse.data.links as Record<string, any>;
            const linkUrl = linksObj.audioWavUrl || linksObj.url || linksObj.wav_url || linksObj.wavUrl || linksObj.download_url;
            if (linkUrl && typeof linkUrl === 'string' && linkUrl.trim()) {
              wavUrl = linkUrl.trim();
              console.log('[Download] WAV URL found in links object, breaking loop', { wavUrl, attempt: attempt + 1 });
              break;
            }
          }
        }

        // If we got an error message indicating failure, throw
        if (statusResponse.data?.message && statusResponse.data.message.toLowerCase().includes('failed')) {
          throw new Error(statusResponse.data.message);
        }
        
        // If we still don't have a URL, continue polling
        if (!wavUrl) {
          console.log('[Download] WAV URL not found yet, continuing to poll...', { attempt: attempt + 1 });
        }
      } catch (pollError: any) {
        // If it's a 502 error with "Failed to fetch Suno task details", continue polling
        if (pollError?.response?.status === 502 && pollError?.response?.data?.message?.includes('Failed to fetch Suno task details')) {
          console.log('[Download] WAV still processing, continuing to poll...', { attempt: attempt + 1 });
          continue;
        }
        // For other errors, check if we have a URL before throwing
        if (!wavUrl) {
          throw pollError;
        }
      }
    }

    if (!wavUrl || typeof wavUrl !== 'string') {
      throw new Error('WAV conversion timed out or URL not available');
    }

    console.log('[Download] WAV URL received', {
      trackId: track.id,
      wavUrl,
    });

    // Step 3: Download WAV file using downloadFile utility
    const filename = `${track.title || 'track'}.wav`.replace(/[^a-z0-9.-]/gi, '_');
    await downloadFile(wavUrl, filename);
    
    console.log('[Download] WAV download initiated', { filename, wavUrl });
  } catch (error) {
    console.error('[Download] Failed to download WAV', error);
    throw error;
  }
}

export async function fetchSunoMiniPrompts(count?: number): Promise<string[]> {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  try {
    const response = await axios.get(
      `${SUNO_API_BASE_URL}${API_URLS.SunoMiniPrompts}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        params: count ? { count } : undefined,
      }
    );
    const prompts = normalizeSuggestionList(response.data?.data?.prompts);
    return prompts;
  } catch (error) {
    console.error("Failed to fetch Suno mini prompts", error);
    throw error;
  }
}

export async function fetchSunoStyles(count?: number): Promise<string[]> {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  try {
    const response = await axios.get(
      `${SUNO_API_BASE_URL}${API_URLS.SunoStyles}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        params: count ? { count } : undefined,
      }
    );
    const styles = normalizeSuggestionList(response.data?.data?.styles);
    return styles;
  } catch (error) {
    console.error("Failed to fetch Suno styles", error);
    throw error;
  }
}

export async function generateSunoSongConcept(options: {
  text?: string;
  improveExisting?: boolean;
}): Promise<string[]> {
  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  try {
    const response = await axios.post(
      `${SUNO_API_BASE_URL}${API_URLS.SunoSongConcept}`,
      {
        text: options.text ?? "",
        improveExisting: Boolean(options.improveExisting),
      },
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.debug("[SunoGPT] raw lyrics response", response.data);

    const payload = response.data;
    let rawSuggestions: unknown = payload?.data;

    // If API returns an object instead of array/string, use its values for normalization
    if (rawSuggestions && !Array.isArray(rawSuggestions) && typeof rawSuggestions === "object") {
      rawSuggestions = Object.values(rawSuggestions as Record<string, unknown>);
    }

    let suggestions = normalizeSuggestionList(rawSuggestions);

    // Fallback to message field if no suggestions and message exists
    if (!suggestions.length && typeof payload?.message === "string" && payload.message.trim()) {
      suggestions = [payload.message.trim()];
    }

    console.debug("[SunoGPT] normalized lyrics suggestions", suggestions);

    return suggestions;
  } catch (error) {
    console.error("Failed to generate Suno song concept", error);
    throw error;
  }
}









// ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ ТРЕКИ 
/**
 * Получает числовой ID плейлиста для API запросов.
 */
function getNumericPlaylistId(playlist: Playlist): number | null {
  if (playlist.remoteId && typeof playlist.remoteId === 'number') {
    return playlist.remoteId;
  }
  if (playlist.remoteId && typeof playlist.remoteId === 'string') {
    const parsed = parseInt(playlist.remoteId, 10);
    if (!isNaN(parsed)) return parsed;
  }
  return null;
}

/**
 * Добавляет трек в плейлист на сервере.
 * После успешного добавления обновляет данные плейлистов с сервера.
 * 
 * @param playlistId - ID плейлиста
 * @param trackId - ID трека для добавления
 * @returns Promise, который разрешается при успешном добавлении
 */
export async function addTrackToPlaylist(playlistId: string, trackId: string): Promise<boolean> {
  const db = loadDb();
  const playlist = db.playlists[playlistId];
  const track = db.tracks[trackId];
  
  if (!playlist || !track) return false;
  if (playlist.trackIds.includes(trackId)) return false; // Already in playlist

  const numericPlaylistId = getNumericPlaylistId(playlist);
  const numericTrackId = getNumericTrackId(track);
  
  if (!numericPlaylistId || !numericTrackId) {
    throw new Error('Playlist or track numeric ID not found');
  }

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  
  console.log('[addTrackToPlaylist] Adding track to playlist:', {
    playlistId,
    trackId,
    numericPlaylistId,
    numericTrackId,
  });
  
  try {
    const url = `${SUNO_API_BASE_URL}${API_URLS.SunoPlaylistTrack
      .replace('{playlist}', numericPlaylistId.toString())
      .replace('{track}', numericTrackId.toString())}`;
    
    console.log('[addTrackToPlaylist] Sending POST request:', {
      url,
      method: 'POST',
    });
    
    const response = await axios.post(
      url,
      {}, // Empty body as per API spec
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    
    console.log('[addTrackToPlaylist] Server response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
    });
    
    // Refresh playlists from server after adding track
    await fetchPlaylists();
    return true;
  } catch (error: any) {
    console.error('[addTrackToPlaylist] Failed to add track to playlist on server', {
      error,
      errorResponse: error?.response?.data,
      errorStatus: error?.response?.status,
      errorStatusText: error?.response?.statusText,
      playlistId,
      trackId,
      numericPlaylistId,
      numericTrackId,
    });
    throw error;
  }
}

/**
 * Удаляет трек из плейлиста на сервере.
 * После успешного удаления обновляет данные плейлистов с сервера.
 * 
 * @param playlistId - ID плейлиста
 * @param trackId - ID трека для удаления из плейлиста
 */
export async function removeTrackFromPlaylist(playlistId: string, trackId: string): Promise<void> {
  const db = loadDb();
  const playlist = db.playlists[playlistId];
  const track = db.tracks[trackId];
  
  if (!playlist || !track) return;

  const numericPlaylistId = getNumericPlaylistId(playlist);
  const numericTrackId = getNumericTrackId(track);
  
  if (!numericPlaylistId || !numericTrackId) {
    throw new Error('Playlist or track numeric ID not found');
  }

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  
  try {
    await axios.delete(
      `${SUNO_API_BASE_URL}${API_URLS.SunoPlaylistTrack
        .replace('{playlist}', numericPlaylistId.toString())
        .replace('{track}', numericTrackId.toString())}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    
    // Refresh playlists from server after removing track
    await fetchPlaylists();
  } catch (error: any) {
    console.error('[removeTrackFromPlaylist] Failed to remove track from playlist on server', error);
    throw error;
  }
}

/**
 * Получает числовой ID трека для API запросов.
 * Ищет ID в params.sunoTrackId, params.id, params.trackId, или пытается преобразовать trackId в число.
 * 
 * @param track - Трек для получения числового ID
 * @returns Числовой ID трека или null, если не найден
 */
function getNumericTrackId(track: Track): number | null {
  // Try to get from params
  if (track.params) {
    // First check sunoTrackId (most common location)
    if (typeof track.params.sunoTrackId === 'number') {
      return track.params.sunoTrackId;
    }
    if (typeof track.params.sunoTrackId === 'string') {
      const parsed = parseInt(track.params.sunoTrackId, 10);
      if (!isNaN(parsed)) return parsed;
    }
    
    // Check id
    if (typeof track.params.id === 'number') {
      return track.params.id;
    }
    if (typeof track.params.id === 'string') {
      const parsed = parseInt(track.params.id, 10);
      if (!isNaN(parsed)) return parsed;
    }
    
    // Check trackId
    if (typeof track.params.trackId === 'number') {
      return track.params.trackId;
    }
    if (typeof track.params.trackId === 'string') {
      const parsed = parseInt(track.params.trackId, 10);
      if (!isNaN(parsed)) return parsed;
    }
  }
  
  // Try to parse track.id if it's numeric
  const parsed = parseInt(track.id, 10);
  if (!isNaN(parsed)) return parsed;
  
  return null;
}

/**
 * Удаляет трек на сервере (отправляет в корзину).
 * После успешного удаления обновляет данные с сервера.
 * 
 * @param trackId - ID трека для удаления (строковый UUID)
 * @returns Promise, который разрешается при успешном удалении
 */
export async function moveTrackToTrash(trackId: string): Promise<void> {
  const db = loadDb();
  const track = db.tracks[trackId];
  
  if (!track) {
    console.error('[moveTrackToTrash] Track not found:', { trackId });
    throw new Error('Track not found');
  }
  
  const numericId = getNumericTrackId(track);
  if (!numericId) {
    // Локальный трек, который ещё не имеет server ID — удаляем только локально
    console.warn('[moveTrackToTrash] Track numeric ID not found, trashing locally only:', {
      trackId: track.id,
      trackParams: track.params,
    });
    try {
      track.trashed = true;
      saveDb(db);
      console.log('[moveTrackToTrash] Track trashed locally (no numeric ID).');
    } catch (error) {
      console.error('[moveTrackToTrash] Failed to trash track locally without numeric ID', {
        trackId: track.id,
        error,
      });
      throw error;
    }
    return;
  }

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  
  console.log('[moveTrackToTrash] Deleting track:', {
    trackId,
    numericTrackId: numericId,
  });
  
  try {
    const url = `${SUNO_API_BASE_URL}${API_URLS.SunoTrack.replace('{track}', numericId.toString())}`;
    
    console.log('[moveTrackToTrash] Sending DELETE request:', {
      url,
      method: 'DELETE',
    });
    
    const response = await axios.delete(
      url,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    console.log('[moveTrackToTrash] Server response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      dataStatus: response.data?.status,
      dataSuccess: response.data?.success,
      dataDataStatus: response.data?.data?.status,
      fullResponse: JSON.stringify(response.data, null, 2),
    });

    // Check response structure - API returns { status: true, data: { status: "ok" }, message: "" }
    // or { success: true, data: { status: "ok" }, message: "" }
    const hasSuccessOk = response.data?.success === true;
    const hasStatusOk = response.data?.status === true;
    const hasDataStatusOk = response.data?.data?.status === 'ok';
    const isHttpOk = response.status === 200;
    
    console.log('[moveTrackToTrash] Response validation:', {
      hasSuccessOk,
      hasStatusOk,
      hasDataStatusOk,
      isHttpOk,
      responseData: response.data,
    });
    
    const isSuccess = hasSuccessOk || hasStatusOk || hasDataStatusOk || isHttpOk;
    
    if (!isSuccess) {
      console.error('[moveTrackToTrash] Server returned unsuccessful status:', {
        responseData: response.data,
        responseDataSuccess: response.data?.success,
        responseDataStatus: response.data?.status,
        responseDataDataStatus: response.data?.data?.status,
        httpStatus: response.status,
        message: response.data?.message,
      });
      throw new Error(response.data?.message || 'Failed to delete track');
    }
    
    console.log('[moveTrackToTrash] Successfully deleted track on server, refreshing data from server...');
    
    // Refresh workspaces and playlists from server after deletion to get updated track lists
    await Promise.all([
      fetchWorkspaces(),
      fetchPlaylists(),
    ]);
    
    console.log('[moveTrackToTrash] Data refreshed after deletion');
  } catch (error: any) {
    console.error('[moveTrackToTrash] Failed to delete track on server', {
      error,
      errorResponse: error?.response?.data,
      errorStatus: error?.response?.status,
      errorStatusText: error?.response?.statusText,
      trackId,
      numericTrackId: numericId,
    });

    // Если на сервере нет такого трека (например, 404) — помечаем его как удалённый локально
    const status = error?.response?.status;
    if (status === 404) {
      console.warn('[moveTrackToTrash] Server reports track not found (404). Trashing locally.', {
        trackId,
        numericTrackId: numericId,
      });
      try {
        const localDb = loadDb();
        const localTrack = localDb.tracks[trackId];
        if (localTrack) {
          localTrack.trashed = true;
          saveDb(localDb);
          console.log('[moveTrackToTrash] Track trashed locally after 404 from server.');
          return;
        }
      } catch (localError) {
        console.error('[moveTrackToTrash] Failed to trash track locally after 404', {
          trackId,
          numericTrackId: numericId,
          error: localError,
        });
      }
    }

    throw error;
  }
}
/**
 * Создает новый трек и добавляет его в указанную рабочую область.
 * Генерирует уникальный ID и временную метку создания. Автоматически определяет
 * флаг hasPersonas на основе наличия текста в поле personas. Проверяет лимит
 * в 100 треков на workspace. По умолчанию fullSongs устанавливается в true.
 * 
 * @param workspaceId - ID рабочей области для добавления трека
 * @param track - Данные трека без id и createdAt (они генерируются автоматически)
 * @returns Созданный объект трека или null, если workspace не найден или превышен лимит
 */
export function addTrackToWorkspace(workspaceId: string, track: Omit<Track, "id" | "createdAt">): Track | null {
  const db = loadDb();
  const ws = db.workspaces[workspaceId];
  if (!ws) return null;
  if (ws.trackIds.length >= 100) return null;
  const id = generateId("trk");
  const personasValue = track.personas || '';
  const hasPersonasValue = personasValue.trim().length > 0;
  
  const fullTrack: Track = { 
    id, 
    title: track.title, 
    createdAt: Date.now(), 
    description: track.description, 
    params: track.params, 
    durationSec: track.durationSec, 
    reaction: null,
    personas: personasValue,
    hasPersonas: hasPersonasValue,
    fullSongs: track.fullSongs !== undefined ? track.fullSongs : true, // default true
  };
  db.tracks[id] = fullTrack;
  ws.trackIds.push(id);
  saveDb(db);
  return fullTrack;
}

/**
 * Устанавливает реакцию пользователя на трек на сервере (лайк, дизлайк или снятие реакции).
 * После успешной установки реакции обновляет данные плейлистов с сервера для синхронизации
 * с плейлистом "Liked Songs".
 * 
 * @param trackId - ID трека для установки реакции
 * @param reaction - Реакция: 'like', 'dislike' или null (снятие реакции)
 */
export async function setTrackReaction(trackId: string, reaction: Reaction): Promise<void> {
  const db = loadDb();
  const track = db.tracks[trackId];
  if (!track) {
    console.error('[setTrackReaction] Track not found in local DB:', trackId);
    throw new Error('Track not found');
  }
  
  console.log('[setTrackReaction] Track data:', {
    trackId,
    trackIdType: typeof track.id,
    params: track.params,
    paramsKeys: track.params ? Object.keys(track.params) : null,
    sunoTrackId: track.params?.sunoTrackId,
    id: track.params?.id,
    trackIdParam: track.params?.trackId,
  });
  
  const numericTrackId = getNumericTrackId(track);
  if (!numericTrackId) {
    console.error('[setTrackReaction] Track numeric ID not found. Track data:', {
      trackId,
      track: JSON.parse(JSON.stringify(track)),
    });
    throw new Error('Track numeric ID not found');
  }
  
  console.log('[setTrackReaction] Found numeric track ID:', numericTrackId);

  // Save current reaction for rollback in case of error
  const previousReaction: Reaction = track.reaction || null;
  
  // Optimistically update UI immediately
  updateTrack(trackId, (track) => {
    track.reaction = reaction;
  });
  
  // Dispatch event for immediate UI update
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('trackReactionChanged', { 
      detail: { trackId, reaction, previousReaction } 
    }));
  }
  
  console.log('[setTrackReaction] Optimistically updated local track reaction:', {
    trackId,
    previousReaction,
    newReaction: reaction,
  });

  const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM) ?? "123";
  
  // Prepare reaction value - API expects "like" or "dislike", not null
  // If reaction is null, we might need to send null or empty string - let's send null for now
  const reactionValue = reaction || null;
  
  const url = `${SUNO_API_BASE_URL}${API_URLS.SunoTrackReaction.replace('{track}', numericTrackId.toString())}`;
  const payload = { reaction: reactionValue };
  
  console.log('[setTrackReaction] Sending request:', {
    url,
    method: 'PATCH',
    payload,
    numericTrackId,
    reaction,
  });
  
  try {
    const response = await axios.patch(
      url,
      payload,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    console.log('[setTrackReaction] Server response:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      headers: response.headers,
    });

    // Server returns: { success: true, data: { status: 'ok' } }
    if (!response.data?.success) {
      console.error('[setTrackReaction] Server returned unsuccessful status:', {
        responseData: response.data,
        message: response.data?.message,
      });
      // Rollback to previous reaction on error
      updateTrack(trackId, (track) => {
        track.reaction = previousReaction;
      });
      
      // Dispatch event for immediate UI rollback
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('trackReactionChanged', { 
          detail: { trackId, reaction: previousReaction, previousReaction: reaction } 
        }));
      }
      
      console.log('[setTrackReaction] Rolled back reaction due to error:', {
        trackId,
        rolledBackTo: previousReaction,
      });
      throw new Error(response.data?.message || 'Failed to set track reaction');
    }
    
    // Success - reaction is already updated optimistically
    console.log('[setTrackReaction] Successfully set track reaction:', {
      trackId,
      reaction,
    });
    
    // Refresh playlists from server after setting reaction to sync "Liked Songs"
    await fetchPlaylists();
    
    // Also refresh workspaces to get updated track data from server
    await fetchWorkspaces();
  } catch (error: any) {
    // Rollback to previous reaction on error
    updateTrack(trackId, (track) => {
      track.reaction = previousReaction;
    });
    
    // Dispatch event for immediate UI rollback
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('trackReactionChanged', { 
        detail: { trackId, reaction: previousReaction, previousReaction: reaction } 
      }));
    }
    
    console.error('[setTrackReaction] Failed to set track reaction on server, rolled back:', {
      trackId,
      numericTrackId,
      reaction,
      previousReaction,
      url,
      payload,
      errorResponse: error?.response?.data,
      errorStatus: error?.response?.status,
      errorStatusText: error?.response?.statusText,
      errorMessage: error?.message,
      fullError: error,
    });
    throw error;
  }
}


